import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ListItem from "@mui/material/ListItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import StarRounded from "@mui/icons-material/StarRounded";
import { playerHandleSuffix } from "@bw/shared/components";
import type { BookingParticipantResponse } from "@bw/shared/types/generated";

/** Server-truth participant row with no raw-UID fallback. */
export function ParticipantRow({
  participant,
  removeSlot,
  onPeek,
}: {
  participant: BookingParticipantResponse;
  removeSlot?: React.ReactNode;
  onPeek?: (userId: string, displayName: string) => void;
}) {
  const name = participant.display_name;
  const isHost = participant.role === "host";
  const checkedIn = Boolean(participant.checked_in_at);
  const roleWord = isHost ? "Host" : "Guest";

  return (
    <ListItem
      component="li"
      divider
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1.25,
        gap: 1.5,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{ minWidth: 0, flex: 1, alignItems: "center" }}
      >
        {name && onPeek ? (
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={() => onPeek(participant.user_id, name)}
            aria-label={`View ${name}`}
            sx={{
              minWidth: 0,
              maxWidth: "100%",
              px: 0,
              justifyContent: "flex-start",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </Button>
        ) : (
          <Typography sx={{ fontWeight: 700 }}>{name ?? "—"}</Typography>
        )}

        {name ? (
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {playerHandleSuffix(participant.user_id)}
          </Typography>
        ) : null}

        <Box
          role="img"
          aria-label={`${roleWord} of this booking`}
          title={roleWord}
          sx={{
            width: 28,
            height: 28,
            display: "grid",
            placeItems: "center",
            borderRadius: isHost ? 1 : "50%",
            bgcolor: isHost ? "primary.main" : "transparent",
            color: isHost ? "primary.contrastText" : "text.secondary",
            border: 1,
            borderColor: isHost ? "primary.main" : "divider",
            flexShrink: 0,
          }}
        >
          {isHost ? (
            <StarRounded fontSize="small" aria-hidden="true" />
          ) : (
            <RadioButtonUncheckedRounded fontSize="small" aria-hidden="true" />
          )}
        </Box>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", flexShrink: 0 }}
      >
        {checkedIn ? (
          <Chip
            icon={<CheckCircleRounded aria-hidden="true" />}
            label="Checked in"
            color="success"
            variant="outlined"
            size="small"
          />
        ) : !isHost ? (
          <Chip label="Going" variant="outlined" size="small" />
        ) : null}
        {removeSlot}
      </Stack>
    </ListItem>
  );
}
